import React, { useState, useEffect, useRef, useMemo } from "react";
import VerticalListItem from "../components/VerticalListItem";
import { Search, X } from "lucide-react";

const SearchPage = ({
  handleItemClick,
  handleBackAction,
  showOverlayHeader,
  numbers,
  isActive,
}) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const inputRef = useRef(null);

  // Debounce: only run search 300ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (isActive && inputRef.current) {
      const timer = setTimeout(() => inputRef.current.focus(), 150);
      return () => clearTimeout(timer);
    }
  }, [isActive]);

  const getTopRatedItems = (array, count = 6) =>
    [...array].sort((a, b) => b.rate - a.rate).slice(0, count);

  // Multi-word ranked search: splits query into tokens, requires ALL to match,
  // ranks by where the match occurs (name > keywords > author > description)
  const filteredItems = useMemo(() => {
    const term = debouncedQuery.trim();
    if (!term) return [];

    const tokens = term.toLowerCase().split(/\s+/).filter(Boolean);

    return numbers
      .map((item) => {
        const name        = (item.name        || "").toLowerCase();
        const keywords    = (item.gameKeywords || "").toLowerCase();
        const author      = (item.author       || "").toLowerCase();
        const description = (item.description  || "").toLowerCase();

        const allMatch = tokens.every(
          (t) => name.includes(t) || keywords.includes(t) || author.includes(t) || description.includes(t)
        );
        if (!allMatch) return null;

        let score = 0;
        tokens.forEach((t) => {
          if (name.startsWith(t))    score += 5;
          if (name.includes(t))      score += 3;
          if (keywords.includes(t))  score += 2;
          if (author.includes(t))    score += 1;
        });

        return { item, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .map(({ item }) => item);
  }, [numbers, debouncedQuery]);

  return (
    <div className="flex flex-col relative w-full min-h-screen bg-gradient-to-r from-semi-blue to-light-blue">

      {/* Header */}
      <div className="px-4 pt-6 pb-2">
        <h1 className="text-white semiBold text-3xl tracking-wide mb-4">Search</h1>

        {/* Search input */}
        <div className="relative flex items-center">
          <Search className="absolute left-4 w-5 h-5 text-white/50 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            placeholder="Games, apps, mods..."
            onChange={(e) => setQuery(e.target.value)}
            className="w-full bg-white/10 backdrop-blur-sm text-white placeholder-white/40 rounded-2xl pl-12 pr-10 py-3 text-base outline-none border border-white/20 focus:border-white/60 focus:bg-white/15 transition-all duration-200"
          />
          {query.length > 0 && (
            <button
              className="absolute right-3 bg-white/20 hover:bg-white/30 rounded-full p-1 transition-colors"
              onClick={() => setQuery("")}
            >
              <X className="w-3.5 h-3.5 text-white" />
            </button>
          )}
        </div>
      </div>

      {showOverlayHeader && (
        <div className="fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-md md:max-w-lg h-16 z-40 backdrop-blur-lg animate-appear flex items-center justify-center">
          <h1 className="text-white medium text-2xl tracking-wider">Search</h1>
        </div>
      )}

      {/* Results */}
      <div className="mt-4">
        {debouncedQuery.length > 0 ? (
          <>
            <div className="flex flex-row justify-between items-baseline mx-4 mb-2">
              <h2 className="text-white medium text-2xl">
                {filteredItems.length > 0 ? `${filteredItems.length} Results` : "No Results"}
              </h2>
            </div>
            {filteredItems.length === 0 ? (
              <div className="text-white/60 text-center my-10 medium text-base px-8">
                No results for "{debouncedQuery}"
              </div>
            ) : (
              filteredItems.map((item) => (
                <VerticalListItem
                  key={item.id}
                  game={item}
                  handleItemClick={handleItemClick}
                  isForSearch={true}
                />
              ))
            )}
          </>
        ) : (
          <>
            <div className="flex flex-row justify-between items-baseline mx-4 mb-2">
              <h2 className="text-white medium text-2xl">Suggested</h2>
            </div>
            {getTopRatedItems(numbers).map((item) => (
              <VerticalListItem
                key={item.id}
                game={item}
                handleItemClick={handleItemClick}
              />
            ))}
          </>
        )}
      </div>

      <div className="h-20" />
    </div>
  );
};

export default SearchPage;
